export type TargetingMap = Record<string, string[]>;

/**
 * Extracts and merges page-level + slot-level GAM targeting from googletag.pubads()
 * and normalizes values to string arrays (deduped).
 *
 * Safely handles:
 * - window undefined (SSR)
 * - googletag undefined
 * - pubads missing
 * - no slots
 * - no targeting
 */
export function getMergedTargeting(
  options: { preferSlotId?: string } = {},
): TargetingMap {
  if (typeof window === "undefined") {
    return {};
  }

  const googletag = window.googletag;
  if (!googletag?.pubads) {
    return {};
  }

  const pubads = googletag.pubads();

  const mergedSets: Record<string, Set<string>> = {};

  const MULTI_VALUE_KEYS = new Set(["news-tags", "learn-tags"]);

  const addValues = (key: string, values: unknown) => {
    const arr = Array.isArray(values) ? values : values != null ? [values] : [];

    if (!mergedSets[key]) {
      mergedSets[key] = new Set<string>();
    }

    for (const value of arr) {
      const raw = String(value).trim();
      if (!raw) {
        continue;
      }

      const parts =
        MULTI_VALUE_KEYS.has(key) && raw.includes(",")
          ? raw
              .split(",")
              .map((part) => part.trim())
              .filter(Boolean)
          : [raw];

      for (const part of parts) {
        mergedSets[key].add(part);
      }
    }
  };

  const pageKeys: string[] = Array.isArray(pubads.getTargetingKeys?.())
    ? pubads.getTargetingKeys()
    : [];

  for (const key of pageKeys) {
    addValues(key, pubads.getTargeting?.(key));
  }

  const slots = Array.isArray(pubads.getSlots?.()) ? pubads.getSlots() : [];

  const preferredSlot = options.preferSlotId
    ? slots.find((slot) => slot?.getSlotElementId?.() === options.preferSlotId)
    : undefined;

  const primarySlot = preferredSlot ?? slots[0];

  const slotMap = primarySlot?.getTargetingMap?.();
  if (slotMap && typeof slotMap === "object") {
    for (const [key, value] of Object.entries(slotMap)) {
      addValues(key, value);
    }
  }

  const result: TargetingMap = {};
  for (const [key, set] of Object.entries(mergedSets)) {
    if (set.size) {
      result[key] = Array.from(set);
    }
  }

  return result;
}
